"use client";

import Link from "next/link";
import { Calculator, Download, ShieldCheck, GraduationCap, ArrowRight, Brain, Zap } from "lucide-react";
import type { Tool } from "@/types";

export function PillarTool({ allTools }: { allTools: Tool[] }) {
  const tool = allTools.find((t) => t.slug === "math-question-generator");
  if (!tool) return null;

  const href = tool.customUrl ?? `/tools/${tool.slug}`;

  return (
    <section className="border-t border-[var(--color-border)] bg-[var(--color-accent-soft)]/30" aria-labelledby="pillar-heading">
      <div className="container py-8 md:py-10">
        <div
          className="group relative flex flex-col md:flex-row items-start md:items-center gap-5 rounded-[var(--radius-xl)] border border-[var(--color-accent)]/50 bg-[var(--color-bg)] p-5 md:p-6 transition-all duration-300 hover:border-[var(--color-accent)] hover:shadow-[0_0_0_1px_var(--color-accent)] hover:bg-[var(--color-accent-soft)]/50"
        >
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-[var(--radius-lg)] bg-[var(--color-accent)] text-[var(--color-signal)] shadow-[var(--shadow-md)]" aria-hidden="true">
            <Calculator className="h-6 w-6" />
          </div>

          <div className="flex-1 min-w-0 text-center md:text-left">
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-2">
              <span className="inline-flex items-center gap-1 rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1 text-xs font-medium text-[var(--color-text-muted)]">
                {tool.category}
              </span>
              {tool.trending && (
                <span className="inline-flex items-center gap-1 rounded-full border border-[var(--color-warning)]/50 bg-[var(--color-warning)]/10 px-3 py-1 text-xs font-semibold text-[var(--color-warning-800)]">
                  <Zap className="h-3 w-3" aria-hidden="true" />
                  Trending
                </span>
              )}
            </div>

            <h2 id="pillar-heading" className="mt-3 text-xl md:text-2xl font-bold tracking-tight text-[var(--color-text)] transition-colors">
              <Link href={href} className="rounded-[var(--radius-sm)] transition-colors group-hover:text-[var(--color-accent)] hover:underline hover:underline-offset-4">
                {tool.name}
              </Link>
            </h2>

            <p className="mt-2 text-sm md:text-base text-[var(--color-text-muted)] max-w-2xl mx-auto md:mx-0">
              {tool.description}
            </p>

            <div className="mt-4 flex flex-wrap items-center justify-center md:justify-start gap-3">
              <Link
                href={href}
                className="inline-flex h-10 items-center gap-2 rounded-full bg-[var(--color-accent)] px-5 text-sm font-semibold text-[var(--color-accent-fg)] shadow-[var(--shadow-sm)] transition-all duration-200 hover:bg-[var(--color-accent-hover)] hover:shadow-[var(--shadow-md)]"
              >
                <GraduationCap className="h-4 w-4" aria-hidden="true" />
                Open Math Question Generator
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
              </Link>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-center md:justify-start gap-3 text-sm text-[var(--color-text-muted)]">
              <span className="flex items-center gap-1.5">
                <Brain className="h-4 w-4 text-[var(--color-accent)]" aria-hidden="true" />
                India, US & General curricula
              </span>
              <span className="flex items-center gap-1.5">
                <Zap className="h-4 w-4 text-[var(--color-accent)]" aria-hidden="true" />
                Step-by-step solutions
              </span>
              <span className="flex items-center gap-1.5">
                <Download className="h-4 w-4 text-[var(--color-accent)]" aria-hidden="true" />
                Printable PDF worksheets
              </span>
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4 text-[var(--color-accent)]" aria-hidden="true" />
                100% client-side
              </span>
            </div>
          </div>

          <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200" aria-hidden="true">
            <ArrowRight className="h-6 w-6 text-[var(--color-accent)]" />
          </div>
        </div>
      </div>
    </section>
  );
}